import React, { useEffect, useState } from "react";
import AppMenu from "../components/AppMenu";
import { title } from "../config";
import { getSiteData, getSSLExpiringSites, getDomainExpiringSites } from "../services/siteAPI";
import { getServers } from "../services/serverAPI";
import moment from "moment";
import { useToast } from "../components/ToastContext";
import "../assets/css/Home.css";

const Home = () => {
  const { showToast } = useToast();
  const [total, setTotal] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [runningCount, setRunningCount] = useState(0);
  const [servers, setServers] = useState([]);
  const [loader, setLoader] = useState(true);

  const [sslExpiryCounts, setSslExpiryCounts] = useState({
    ssl15: 0,
    ssl30: 0,
    ssl45: 0,
    ssl60: 0,
  });

  const [domainExpiryCounts, setDomainExpiryCounts] = useState({
    domain15: 0,
    domain30: 0,
    domain45: 0,
    domain60: 0,
  });

  const [serverAlerts, setServerAlerts] = useState({
    ram90: 0,
    ram80: 0,
    disk90: 0,
    disk80: 0,
  });

  useEffect(() => {
    reloadDashboard();
  }, []);

  const reloadDashboard = async () => {
    try {
      setLoader(true);
      const [sitesData, serversData, exp15, exp30, exp45, exp60, ssl15Data, ssl30Data, ssl45Data, ssl60Data] = await Promise.all([
        getSiteData(showToast),
        getServers(showToast),
        getDomainExpiringSites(15, showToast),
        getDomainExpiringSites(30, showToast),
        getDomainExpiringSites(45, showToast),
        getDomainExpiringSites(60, showToast),
        getSSLExpiringSites(15, showToast),
        getSSLExpiringSites(30, showToast),
        getSSLExpiringSites(45, showToast),
        getSSLExpiringSites(60, showToast),
      ]);

      const sitesList = Array.isArray(sitesData) ? sitesData : [];
      const serversList = Array.isArray(serversData) ? serversData : [];

      setTotal(sitesList.length);
      setServers(serversList);

      let running = 0;
      let errors = 0;

      sitesList.forEach((site) => {
        if (site.runningStatus === true) running++;
        else if (site.runningStatus === false || site.status === "error") errors++;
      });

      setRunningCount(running);
      setErrorCount(errors);

      setDomainExpiryCounts({
        domain15: exp15?.length || 0,
        domain30: exp30?.length || 0,
        domain45: exp45?.length || 0,
        domain60: exp60?.length || 0,
      });

      setSslExpiryCounts({
        ssl15: ssl15Data?.length || 0,
        ssl30: ssl30Data?.length || 0,
        ssl45: ssl45Data?.length || 0,
        ssl60: ssl60Data?.length || 0,
      });

      // Calculate Server Resource Alerts
      let r90 = 0;
      let r80 = 0;
      let d90 = 0;
      let d80 = 0;

      serversList.forEach((server) => {
        const ram = server.ramUsagePercent;
        if (ram !== undefined) {
          if (ram >= 90) r90++;
          else if (ram >= 80) r80++;
        }

        const disk = server.diskUsagePercent;
        if (disk !== undefined) {
          if (disk >= 90) d90++;
          else if (disk >= 80) d80++;
        }
      });

      setServerAlerts({
        ram90: r90,
        ram80: r80,
        disk90: d90,
        disk80: d80,
      });
    } catch (err) {
      console.error("Error loading dashboard data:", err);
    } finally {
      setLoader(false);
    }
  };

  const formatGB = (bytes) => {
    if (!bytes || isNaN(bytes)) return "-";
    return `${(bytes / 1073741824).toFixed(2)} GB`;
  };

  return (
    <div className="dashboard-container">
      <div className="px-2 sticky-header">
        <h3 className="m-0 p-2">{title} | Home</h3>
        <AppMenu />
      </div>

      {loader ? (
        <div className="dashboard-loader text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading SystemQ Dashboard...</p>
        </div>
      ) : (
        <div className="container-fluid py-3">
          {/* Sites & Servers High-Level Summary */}
          <div className="row g-3 mb-3">
            <div className="col-md-3 col-sm-6">
              <div className="alert alert-primary dashboard-card">
                <h5 className="fw-semibold text-primary">All Sites</h5>
                <p className="mb-0 text-dark">
                  <a className="dashboard-link text-primary" href="/site">
                    Total Sites: <strong>{total}</strong>
                  </a>
                </p>
              </div>
            </div>

            <div className="col-md-3 col-sm-6">
              <div className="alert alert-success dashboard-card">
                <h5 className="fw-semibold text-success">Running Sites</h5>
                <p className="mb-0 text-dark">
                  <a className="dashboard-link text-success" href="/sites/runningStatus/true">
                    Running: <strong>{runningCount}</strong>
                  </a>
                </p>
              </div>
            </div>

            <div className="col-md-3 col-sm-6">
              <div className="alert alert-danger dashboard-card">
                <h5 className="fw-semibold text-danger">Error Sites</h5>
                <p className="mb-0 text-dark">
                  <a className="dashboard-link text-danger" href="/sites/runningStatus/false">
                    Errors: <strong>{errorCount}</strong>
                  </a>
                </p>
              </div>
            </div>

            <div className="col-md-3 col-sm-6">
              <div className="alert alert-info dashboard-card">
                <h5 className="fw-semibold text-info">Servers</h5>
                <p className="mb-0 text-dark">
                  <a className="dashboard-link text-info" href="/servers">
                    Total Servers: <strong>{servers.length}</strong>
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Resource & Expiry Alerts Grid */}
          <div className="row g-3 mb-4">
            {/* Server Resource Alert Badges */}
            <div className="col-md-3 col-sm-6">
              <div className={`alert ${serverAlerts.ram90 > 0 ? "alert-danger" : "alert-light border"} dashboard-card`}>
                <strong>RAM Usage Alerts</strong>
                <div className="mt-2 small">
                  <div className={serverAlerts.ram90 > 0 ? "text-danger fw-bold" : "text-muted"}>
                    <i className="fas fa-exclamation-triangle me-1"></i> RAM &ge; 90%: <strong>{serverAlerts.ram90} Servers</strong>
                  </div>
                  <div className={serverAlerts.ram80 > 0 ? "text-warning fw-bold mt-1" : "text-muted mt-1"}>
                    <i className="fas fa-exclamation-circle me-1"></i> RAM &ge; 80%: <strong>{serverAlerts.ram80} Servers</strong>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-3 col-sm-6">
              <div className={`alert ${serverAlerts.disk90 > 0 ? "alert-danger" : "alert-light border"} dashboard-card`}>
                <strong>Disk Storage Alerts</strong>
                <div className="mt-2 small">
                  <div className={serverAlerts.disk90 > 0 ? "text-danger fw-bold" : "text-muted"}>
                    <i className="fas fa-hdd me-1"></i> Storage &ge; 90%: <strong>{serverAlerts.disk90} Servers</strong>
                  </div>
                  <div className={serverAlerts.disk80 > 0 ? "text-warning fw-bold mt-1" : "text-muted mt-1"}>
                    <i className="fas fa-hdd me-1"></i> Storage &ge; 80%: <strong>{serverAlerts.disk80} Servers</strong>
                  </div>
                </div>
              </div>
            </div>

            {/* SSL Expiry */}
            <div className="col-md-3 col-sm-6">
              <div className="alert alert-warning dashboard-card">
                <strong>SSL Expiry</strong>
                <div className="mt-2 small">
                  <div className={sslExpiryCounts.ssl15 > 0 ? "ssl15-count" : ""}>
                    <a className="dashboard-link" href="/sites/expiry/15">
                      {sslExpiryCounts.ssl15} sites expiring in <strong>15 days</strong>
                    </a>
                  </div>
                  <div className={sslExpiryCounts.ssl30 > 0 ? "ssl30-count" : ""}>
                    <a className="dashboard-link" href="/sites/expiry/30">
                      {sslExpiryCounts.ssl30} sites expiring in <strong>30 days</strong>
                    </a>
                  </div>
                  <div className={sslExpiryCounts.ssl60 > 0 ? "ssl60-count" : ""}>
                    <a className="dashboard-link" href="/sites/expiry/60">
                      {sslExpiryCounts.ssl60} sites expiring in <strong>60 days</strong>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Domain Expiry */}
            <div className="col-md-3 col-sm-6">
              <div className="alert alert-warning dashboard-card">
                <strong>Domain Expiry</strong>
                <div className="mt-2 small">
                  <div className={domainExpiryCounts.domain15 > 0 ? "domain15-count" : ""}>
                    <a className="dashboard-link" href="/sites/domain-expiry/15">
                      {domainExpiryCounts.domain15} domains expiring in <strong>15 days</strong>
                    </a>
                  </div>
                  <div className={domainExpiryCounts.domain30 > 0 ? "domain30-count" : ""}>
                    <a className="dashboard-link" href="/sites/domain-expiry/30">
                      {domainExpiryCounts.domain30} domains expiring in <strong>30 days</strong>
                    </a>
                  </div>
                  <div className={domainExpiryCounts.domain60 > 0 ? "domain60-count" : ""}>
                    <a className="dashboard-link" href="/sites/domain-expiry/60">
                      {domainExpiryCounts.domain60} domains expiring in <strong>60 days</strong>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Servers Live Resource Monitoring Section */}
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0 text-primary">
                <i className="fas fa-server me-2"></i> Server Resource Monitor (RAM & Storage)
              </h5>
              <a href="/servers" className="btn btn-sm btn-outline-primary">
                Manage Servers
              </a>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Server</th>
                      <th>IP / Hostname</th>
                      <th style={{ minWidth: "220px" }}>RAM Usage</th>
                      <th style={{ minWidth: "220px" }}>Disk Storage</th>
                      <th>CPU Load</th>
                      <th>Last Reported</th>
                    </tr>
                  </thead>
                  <tbody>
                    {servers.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-4 text-muted">
                          No servers configured. Visit <a href="/servers">Servers</a> to register a server.
                        </td>
                      </tr>
                    ) : (
                      servers.map((s, idx) => {
                        const ramPercent = s.ramUsagePercent;
                        const isHighRam = ramPercent !== undefined && ramPercent >= 80;
                        const isCriticalRam = ramPercent !== undefined && ramPercent >= 90;

                        const diskPercent = s.diskUsagePercent;
                        const isHighDisk = diskPercent !== undefined && diskPercent >= 80;
                        const isCriticalDisk = diskPercent !== undefined && diskPercent >= 90;

                        return (
                          <tr key={s._id || idx}>
                            <td>
                              <strong>{s.name}</strong>
                              {s.description && <div className="text-muted small">{s.description}</div>}
                            </td>
                            <td>{s.hostname || s.ip || "-"}</td>
                            <td>
                              {s.totalMem ? (
                                <div>
                                  <div className="d-flex justify-content-between small text-muted mb-1" style={{ fontSize: "0.78rem" }}>
                                    <span><strong>Used:</strong> {s.usedMem !== undefined ? `${s.usedMem} MB` : `${s.totalMem - (s.freeMem || 0)} MB`}</span>
                                    <span><strong>Cache:</strong> {s.cacheMem !== undefined ? `${s.cacheMem} MB` : "-"}</span>
                                    <span><strong>Avail:</strong> {s.freeMem || 0} MB</span>
                                  </div>
                                  <div className="progress" style={{ height: "7px" }}>
                                    <div
                                      className={`progress-bar ${isCriticalRam ? "bg-danger" : isHighRam ? "bg-warning" : "bg-primary"}`}
                                      title={`Occupied: ${ramPercent}%`}
                                      style={{ width: `${ramPercent || 0}%` }}
                                    ></div>
                                    {s.cacheMem > 0 && (
                                      <div
                                        className="progress-bar bg-info bg-opacity-75"
                                        title={`Cache: ${Math.round((s.cacheMem / s.totalMem) * 100)}%`}
                                        style={{ width: `${Math.round((s.cacheMem / s.totalMem) * 100)}%` }}
                                      ></div>
                                    )}
                                  </div>
                                  <div className="mt-1 d-flex gap-1 flex-wrap" style={{ fontSize: "0.72rem" }}>
                                    <span className={`badge ${isCriticalRam ? "bg-danger" : isHighRam ? "bg-warning text-dark" : "bg-primary"}`}>
                                      {ramPercent}% Occupied
                                    </span>
                                    {s.cacheMem > 0 && (
                                      <span className="badge bg-secondary">
                                        {Math.round((s.cacheMem / s.totalMem) * 100)}% Cache
                                      </span>
                                    )}
                                    <span className="badge bg-success">
                                      {100 - (ramPercent || 0)}% Available
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td>
                              {s.size ? (
                                <div>
                                  <div className="d-flex justify-content-between small mb-1">
                                    <span>{formatGB(s.free)} Free</span>
                                    <span>{formatGB(s.size)}</span>
                                  </div>
                                  <div className="progress" style={{ height: "7px" }}>
                                    <div
                                      className={`progress-bar ${isCriticalDisk ? "bg-danger" : isHighDisk ? "bg-warning" : "bg-info"}`}
                                      style={{ width: `${diskPercent || 0}%` }}
                                    ></div>
                                  </div>
                                  <span className={`badge mt-1 ${isCriticalDisk ? "bg-danger" : isHighDisk ? "bg-warning text-dark" : "bg-secondary"}`}>
                                    {diskPercent}% full
                                  </span>
                                </div>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td>{s.cpuLoad ? (Array.isArray(s.cpuLoad) ? s.cpuLoad.join(" / ") : s.cpuLoad) : "-"}</td>
                            <td className="small text-muted">
                              {s.lastUpdated ? moment(s.lastUpdated).fromNow() : "-"}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
